/**
 * Centralized Session Refresh Service
 *
 * Provides a consistent, DRY approach to refreshing sidebar sessions
 * across different parts of the application.
 *
 * This service handles:
 * - Timing delays for proper refresh execution
 * - Logging for debugging
 * - Null safety checks
 * - Consistent behavior across all refresh scenarios
 */

interface SessionRefreshOptions {
  delay?: number;
  reason?: string;
  force?: boolean;
}

export class SessionRefreshService {
  private static instance: SessionRefreshService;
  private refreshFunction: (() => void) | null = null;
  private pendingRefreshTimeout: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): SessionRefreshService {
    if (!SessionRefreshService.instance) {
      SessionRefreshService.instance = new SessionRefreshService();
    }
    return SessionRefreshService.instance;
  }

  /**
   * Register the refresh function from the sidebar component
   */
  registerRefreshFunction(refreshFn: () => void): void {
    console.log("🔄 REFRESH_SERVICE: registering refresh function");
    this.refreshFunction = refreshFn;
  }

  /**
   * Unregister the refresh function (useful for cleanup)
   */
  unregisterRefreshFunction(): void {
    console.log("🔄 REFRESH_SERVICE: unregistering refresh function");
    this.refreshFunction = null;
    if (this.pendingRefreshTimeout) {
      clearTimeout(this.pendingRefreshTimeout);
      this.pendingRefreshTimeout = null;
    }
  }

  /**
   * Execute a delayed refresh with consistent logging and error handling
   */
  private executeRefresh(reason: string, delay: number = 100): void {
    if (!this.refreshFunction) {
      console.warn(
        `🔄 REFRESH_SERVICE: cannot refresh - no function registered (reason: ${reason})`
      );
      return;
    }

    console.log(
      `🔄 REFRESH_SERVICE: scheduling refresh in ${delay}ms (reason: ${reason})`
    );

    // Clear any existing pending refresh
    if (this.pendingRefreshTimeout) {
      clearTimeout(this.pendingRefreshTimeout);
    }

    this.pendingRefreshTimeout = setTimeout(() => {
      try {
        console.log(
          `🔄 REFRESH_SERVICE: executing refresh (reason: ${reason})`
        );
        this.refreshFunction!();
        this.pendingRefreshTimeout = null;
      } catch (error) {
        console.error(
          `🔄 REFRESH_SERVICE: error during refresh (reason: ${reason}):`,
          error
        );
      }
    }, delay);
  }

  /**
   * Refresh sidebar sessions after new session creation
   */
  refreshAfterNewSession(options: SessionRefreshOptions = {}): void {
    const { delay = 100, reason = "new session creation" } = options;
    this.executeRefresh(reason, delay);
  }

  /**
   * Refresh sidebar sessions after session rename
   */
  refreshAfterRename(options: SessionRefreshOptions = {}): void {
    const { delay = 100, reason = "session rename" } = options;
    this.executeRefresh(reason, delay);
  }

  /**
   * Refresh sidebar sessions after session deletion
   */
  refreshAfterDelete(options: SessionRefreshOptions = {}): void {
    const { delay = 100, reason = "session deletion" } = options;
    this.executeRefresh(reason, delay);
  }

  /**
   * Refresh sidebar sessions after bulk operations (delete all, etc.)
   */
  refreshAfterBulkOperation(options: SessionRefreshOptions = {}): void {
    const { delay = 150, reason = "bulk operation" } = options;
    this.executeRefresh(reason, delay);
  }

  /**
   * Refresh sidebar sessions after first message (session rename)
   */
  refreshAfterFirstMessage(options: SessionRefreshOptions = {}): void {
    const { delay = 100, reason = "first message session rename" } = options;
    this.executeRefresh(reason, delay);
  }

  /**
   * Immediate refresh (no delay)
   */
  refreshImmediately(reason: string = "immediate"): void {
    this.executeRefresh(reason, 0);
  }

  /**
   * Check if refresh function is registered
   */
  isRefreshFunctionRegistered(): boolean {
    return this.refreshFunction !== null;
  }

  /**
   * Force refresh regardless of registration status (useful for debugging)
   */
  forceRefresh(reason: string = "forced"): void {
    if (this.refreshFunction) {
      console.log(`🔄 REFRESH_SERVICE: force refreshing (reason: ${reason})`);
      try {
        this.refreshFunction();
      } catch (error) {
        console.error(`🔄 REFRESH_SERVICE: error during force refresh:`, error);
      }
    } else {
      console.warn(
        `🔄 REFRESH_SERVICE: cannot force refresh - no function registered`
      );
    }
  }
}

// Export singleton instance
export const sessionRefreshService = SessionRefreshService.getInstance();

// Utility functions for easy access
export const refreshAfterNewSession = (options?: SessionRefreshOptions) =>
  sessionRefreshService.refreshAfterNewSession(options);

export const refreshAfterRename = (options?: SessionRefreshOptions) =>
  sessionRefreshService.refreshAfterRename(options);

export const refreshAfterDelete = (options?: SessionRefreshOptions) =>
  sessionRefreshService.refreshAfterDelete(options);

export const refreshAfterBulkOperation = (options?: SessionRefreshOptions) =>
  sessionRefreshService.refreshAfterBulkOperation(options);

export const refreshAfterFirstMessage = (options?: SessionRefreshOptions) =>
  sessionRefreshService.refreshAfterFirstMessage(options);
